import { forwardRef, useMemo } from 'react';
import { Calendar, Users } from 'lucide-react';
import { format, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

export interface SimplifiedSearchSummaryProps {
  checkIn?: Date;
  checkOut?: Date;
  guests?: string | number;
  onClick?: () => void;
  title?: string;
  className?: string;
  showIcons?: boolean;
  disabled?: boolean;
}

function formatDateRangeES(checkIn?: Date, checkOut?: Date): string {
  if (!checkIn || !checkOut || !isValid(checkIn) || !isValid(checkOut)) {
    return 'Agregar fechas';
  }

  const sameMonth =
    checkIn.getFullYear() === checkOut.getFullYear() &&
    checkIn.getMonth() === checkOut.getMonth();

  const sameYear = checkIn.getFullYear() === checkOut.getFullYear();

  if (sameMonth) {
    const startDay = format(checkIn, 'd', { locale: es });
    const endDay = format(checkOut, 'd', { locale: es });
    const month = format(checkIn, 'MMM', { locale: es });
    return `${startDay}-${endDay} de ${month}`;
  }

  if (sameYear) {
    const left = `${format(checkIn, 'd', { locale: es })} ${format(
      checkIn,
      'MMM',
      { locale: es },
    )}`;

    const right = `${format(checkOut, 'd', { locale: es })} ${format(
      checkOut,
      'MMM',
      { locale: es },
    )}`;
    return `${left} – ${right}`;
  }

  const left = `${format(checkIn, 'd MMM yyyy', { locale: es })}`;

  const right = `${format(checkOut, 'd MMM yyyy', { locale: es })}`;
  return `${left} – ${right}`;
}

function formatGuestsES(guests?: string | number): string {
  const n = Math.max(1, Number.parseInt(String(guests ?? '1'), 10) || 1);
  return n === 1 ? '1 huésped' : `${n} huéspedes`;
}

const SimplifiedSearchSummary = forwardRef<
  HTMLDivElement,
  SimplifiedSearchSummaryProps
>(
  (
    {
      checkIn,
      checkOut,
      guests,
      onClick,
      title = 'Alojamientos en Manzanillo',
      className,
      showIcons = true,
      disabled = false,
    },
    ref,
  ) => {
    const dateText = useMemo(
      () => formatDateRangeES(checkIn, checkOut),
      [checkIn, checkOut],
    );
    const guestsText = useMemo(() => formatGuestsES(guests), [guests]);

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.();
      }
    };

    return (
      <div
        ref={ref}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label="Editar búsqueda"
        onClick={disabled ? undefined : onClick}
        onKeyDown={handleKeyDown}
        className={cn(
          'bg-white rounded-xl shadow-sm border border-gray-200 p-3',
          'transition-shadow mx-2',
          disabled
            ? 'opacity-60 cursor-not-allowed'
            : 'cursor-pointer hover:shadow-md',
          className,
        )}
      >
        <div className="mb-1">
          <h2 className="text-base font-semibold text-gray-900 text-center">
            {title}
          </h2>
        </div>

        <div className="flex justify-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            {showIcons && <Calendar className="h-4 w-4" aria-hidden="true" />}
            <span>{dateText}</span>
          </div>

          <div className="flex items-center gap-1">
            {showIcons && <Users className="h-4 w-4" aria-hidden="true" />}
            <span>{guestsText}</span>
          </div>
        </div>
      </div>
    );
  },
);

SimplifiedSearchSummary.displayName = 'SimplifiedSearchSummary';
export default SimplifiedSearchSummary;
